import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Tag, FolderInput, Plus, Loader2, Check, X, Sparkles } from 'lucide-react';
import { MAIL_API, mailAuth } from './mailUtils';

/** "Label as…" / "Move to…" popover: search labels, create one, or take the suggested job label.
 *  onPick(label) — caller decides add vs move. chips → suggested "26-3092 Customer" label. */
export const LabelPicker = ({ labels, onPick, onCreated, mode = 'label', chips = [], current = [], size = 'sm', disabled, testid = 'mail-label-picker' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const inputRef = useRef(null);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const up = window.innerHeight - r.bottom < 360;
      setPos({ left: Math.min(r.left, window.innerWidth - 300), top: up ? undefined : r.bottom + 6, bottom: up ? window.innerHeight - r.top + 6 : undefined });
    }
    setQ(''); setOpen(o => !o);
  };
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  const suggested = useMemo(() => {
    const c = chips.find(x => x.type === 'wo') || chips[0];
    if (!c) return null;
    const existing = labels.find(l => l.name.toLowerCase().includes(c.ref.toLowerCase()));
    return { chip: c, existing, name: existing ? existing.name : `${c.ref}${c.customer ? ' ' + c.customer : ''}`.trim() };
  }, [chips, labels]);

  const list = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return labels.filter(l => !ql || l.name.toLowerCase().includes(ql)).slice(0, 80);
  }, [labels, q]);
  const exact = labels.some(l => l.name.toLowerCase() === q.trim().toLowerCase());

  const create = async (name) => {
    setBusy(true);
    try {
      const r = await axios.post(`${MAIL_API}/labels`, { name }, mailAuth());
      onCreated?.(r.data);
      toast.success(r.data.created ? `Created label “${r.data.name}”` : `Using label “${r.data.name}”`);
      onPick(r.data); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Could not create label'); }
    setBusy(false);
  };
  const pick = (l) => { onPick(l); setOpen(false); };
  const Icon = mode === 'move' ? FolderInput : Tag;
  const btn = size === 'sm'
    ? 'px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-sky-300 hover:bg-sky-50 disabled:opacity-40 text-xs text-slate-800 font-semibold flex items-center gap-1.5 shadow-sm transition-colors'
    : 'p-2 rounded-lg text-slate-500 hover:bg-slate-900/5 hover:text-slate-900 disabled:opacity-40';

  return (
    <span className="relative inline-flex">
      <button ref={btnRef} onClick={toggle} disabled={disabled} className={btn} title={mode === 'move' ? 'Move to…' : 'Label as…'} data-testid={testid}>
        <Icon className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />{size === 'sm' && (mode === 'move' ? 'Move to' : 'Label')}
      </button>
      {open && pos && createPortal(
        <div className="fixed z-[9500] w-72 rounded-xl border border-slate-200 bg-white shadow-2xl p-2 space-y-1.5" style={pos} onClick={e => e.stopPropagation()} data-testid={`${testid}-popover`}>
          <div className="flex items-center justify-between px-1"><span className="text-xs font-bold text-slate-900">{mode === 'move' ? 'Move to folder' : 'Label as'}</span><button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-900"><X className="w-3.5 h-3.5" /></button></div>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="Find or create a label…" className="w-full text-xs bg-white border border-slate-200 rounded-md px-2 py-1.5 text-slate-900 placeholder:text-slate-400 outline-none focus:border-sky-500" data-testid={`${testid}-search`}
            onKeyDown={e => { if (e.key === 'Enter') { const hit = list[0]; if (q.trim() && !exact) create(q.trim()); else if (hit) pick(hit); } }} />
          {suggested && !q && (
            <button onClick={() => (suggested.existing ? pick(suggested.existing) : create(suggested.name))} disabled={busy} className="w-full text-left px-2 py-1.5 rounded-lg bg-sky-50 border border-sky-200 hover:bg-sky-100 text-xs text-sky-900 flex items-center gap-1.5" data-testid={`${testid}-suggested`}>
              <Sparkles className="w-3.5 h-3.5 text-sky-600 shrink-0" /><span className="truncate">{suggested.existing ? '' : 'Create '}<b>{suggested.name}</b></span>
            </button>
          )}
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {list.map(l => { const on = current.includes(l.id); return (
              <button key={l.id} onClick={() => pick(l)} className="w-full text-left px-2 py-1.5 text-xs text-slate-800 hover:bg-slate-50 flex items-center gap-1.5" style={{ paddingLeft: 8 + l.depth * 12 }} data-testid={`${testid}-opt-${l.id}`}>
                <Tag className="w-3 h-3 text-slate-400 shrink-0" style={l.color ? { color: l.color } : undefined} /><span className="truncate">{l.leaf}</span>{on && <Check className="w-3 h-3 text-emerald-600 ml-auto" />}
              </button>); })}
            {!list.length && !q && <p className="text-[11px] text-slate-500 px-2 py-3">No labels yet — type a name to create one.</p>}
          </div>
          {q.trim() && !exact && (
            <button onClick={() => create(q.trim())} disabled={busy} className="w-full px-2 py-1.5 rounded-lg bg-slate-900 hover:bg-sky-800 text-white text-xs font-semibold flex items-center justify-center gap-1.5" data-testid={`${testid}-create`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}Create “{q.trim()}”
            </button>
          )}
        </div>, document.body)}
    </span>
  );
};
